import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import { InputMessageContainer } from 'components/field/styled/InputCommon.styled';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAddressData from 'hooks/useAddressData';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCED_OMS, SHIPPING_PARTNERS } from 'services/consts';

import { getCityByCountry } from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';

import { faExclamationCircle, faPlus, faTrashCan } from '@fortawesome/pro-solid-svg-icons';

import * as S from './CityGroupForm.styled';
import PolygonSelectionModal from './PolygonSelectionModal';

/**
 * @author @omerrkosar
 */

const formOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const maxPolygonLength = 65535;

const CityGroupForm = NiceModal.create(({ mode, id, modalName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					description: yup.string().nullable(),
					countryName: yup.string().nullable().required(),
					cityName: yup.array().nullable(),
					deviceType: yup.string().nullable(),
					pickupLocationCode: yup.string().nullable(),
					leadTimeInDays: yup.string().nullable(),
					locationArray: yup
						.array()
						.of(
							yup.object().shape({
								city: yup.string().nullable().required(),
								polygon: yup.string().nullable()
							})
						)
						.min(1),
					deliverySlotIds: yup.array().nullable()
				})
				.required(),
		[]
	);
	const { showError, showSuccess } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const [cities, setCities] = useState([]);
	const [deliverySlotList, setDeliverySlotList] = useState([]);
	const [selectedDeliverySlots, setSelectedDeliverySlots] = useState([]);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { countries } = useAddressData();
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();
	const viewMode = mode === 'view';

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: null,
			description: null,
			countryName: null,
			cityName: null,
			deviceType: null,
			pickupLocationCode: null,
			leadTimeInDays: null,
			locationArray: [{ city: null, polygon: null }],
			deliverySlotIds: null
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, errors }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const fillFormInEdit = async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(ADVANCED_OMS.GET_CITY_GROUP, { ID: id });
			const { data } = response;

			reset({
				name: data.name ?? null,
				description: data.description ?? null,
				cityName: data.cityName ?? [],
				countryName: data.countryName ?? null,
				deviceType: data.deviceType ?? null,
				pickupLocationCode: data.pickupLocationCode ?? null,
				leadTimeInDays: data.leadTimeInDays ?? null,
				locationArray: data.locationArray ?? [],
				deliverySlotIds: data.deliverySlotIds ?? []
			});
			if (data.cityName?.length > data.locationArray?.length)
				updateLocationArrayData(data.cityName, data.countryName);
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: `${error.message}`
			});
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (id) {
			fillFormInEdit();
		}
	}, [id]);

	const setDeliverySlotIds = (selectedDeliverySlots) => {
		if (!selectedDeliverySlots) {
			setValue('deliverySlotIds', selectedDeliverySlots, formOptions);
			return;
		}
		const selectedSlotIds = selectedDeliverySlots
			.map(
				(selectedDeliverySlotName) =>
					deliverySlotList.find((deliverySlot) => deliverySlot.name === selectedDeliverySlotName)?.id
			)
			.filter(Boolean);

		setValue('deliverySlotIds', selectedSlotIds, formOptions);
	};

	const onSubmit = (data) => {
		setIsLoading(true);
		const URL = id ? ADVANCED_OMS.UPDATE_CITY_GROUP : ADVANCED_OMS.ADD_CITY_GROUP;

		const selectedCities = locationArray
			.map((location) => cities.find((city) => city.id.toString() === location.city)?.name)
			.filter(Boolean);

		axiosPrivate
			.post(URL, {
				...data,
				cityName: selectedCities ?? [],
				...(id && { ID: id })
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.CITY_GROUP_LIST));
					closeModal();
				} else AppLogger.error(response);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const countryName = watch(`countryName`) ?? null;
	const locationArray = watch(`locationArray`) ?? null;
	const deliverySlotIds = watch(`deliverySlotIds`) ?? null;

	const fetchCitiesByCountry = useCallback(
		async (name) => {
			return await dispatch(getCityByCountry(name))
				.unwrap()
				.then((response) => {
					setCities(response?.data ?? []);
					return response?.data ?? [];
				})
				.catch((error) => {
					AppLogger.error(error);
					return [];
				});
		},
		[countryName]
	);
	const updateLocationArrayData = async (cityName, countryName) => {
		const cities = await fetchCitiesByCountry(countryName);
		const selectedCities = cityName.map((selectedCityName) =>
			cities.find((city) => city.name === selectedCityName)
		);
		if (selectedCities.length > 0) {
			setValue(
				'locationArray',
				selectedCities.map((selectedCity) => ({
					city: selectedCity.id.toString(),
					polygon: null
				})),
				formOptions
			);
		}
	};

	const getDeliverySlots = async () => {
		try {
			const { data, status } = await axiosPrivate.post(SHIPPING_PARTNERS.GET_ALL_DELIVERY_SLOT);
			if (status === 200) {
				setDeliverySlotList(data.deliverySlots);
			} else {
				AppLogger.error(data);
			}
		} catch (error) {
			AppLogger.error(error);
		}
	};

	useEffect(() => {
		const fetchCities = async () => {
			if (countryName) {
				const data = await fetchCitiesByCountry(countryName);
				setCities(data);
			}
		};
		fetchCities();
	}, [countryName]);

	useEffect(() => {
		getDeliverySlots();
	}, []);

	useEffect(() => {
		if (deliverySlotList && id && deliverySlotIds) {
			if (deliverySlotIds) {
				const selectedSlots = deliverySlotIds
					.map((slotId) => deliverySlotList.find((deliverySlot) => deliverySlot.id === slotId)?.name)
					.filter(Boolean);
				setSelectedDeliverySlots(selectedSlots);
			}
		}
	}, [deliverySlotList, id, deliverySlotIds]);

	const defaultColSpan = {
		md: 6
	};

	const smallColSpan = {
		md: 4
	};

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const handleAddCity = () => {
		setValue(
			'locationArray',
			[
				...locationArray,
				{
					city: null,
					polygon: null
				}
			],
			formOptions
		);
	};

	const handleRemoveCity = (index) => {
		setValue(
			'locationArray',
			locationArray.filter((_, i) => i !== index),
			formOptions
		);
	};

	const handleAddPolygon = (index) => {
		const selectedCity = cities.find((city) => city.id.toString() === locationArray[index].city)?.name;
		NiceModal.show(PolygonSelectionModal, {
			cityName: selectedCity,
			viewMode,
			locationArray,
			index,
			setValue,
			id
		});
	};

	const clearLocationArray = () => {
		const newLocationArray = locationArray.map((location) => ({
			...location,
			city: null,
			polygon: null
		}));
		setValue('locationArray', newLocationArray, formOptions);
	};

	return (
		<Modal fullwidth maxWidth="large" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">cityGroupForm</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="name" isLoading={isLoading} required>
						<StringInput name="name" viewMode={viewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="country" isLoading={isLoading} required>
						<EnumSelect
							viewMode={viewMode}
							labelPrefix="country."
							name="countryName"
							value={countryName ?? null}
							data={countries}
							setValue={(value) => {
								clearLocationArray();
								if (value !== countryName) {
									setValue('cityName', null);
								}
								setValue('countryName', value ?? null, formOptions);
							}}
						/>
					</FormItem>
					<FormItem label="description" isLoading={isLoading} colSpan={defaultColSpan}>
						<StringInput name="description" viewMode={viewMode} />
					</FormItem>
					<FormItem label="deviceType" isLoading={isLoading} colSpan={defaultColSpan}>
						<StringInput name="deviceType" viewMode={viewMode} />
					</FormItem>
					<FormItem label="pickupLocationCode" isLoading={isLoading} colSpan={defaultColSpan}>
						<StringInput name="pickupLocationCode" viewMode={viewMode} />
					</FormItem>
					<FormItem label="leadTimeInDays" isLoading={isLoading} colSpan={defaultColSpan}>
						<StringInput name="leadTimeInDays" viewMode={viewMode} />
					</FormItem>

					<S.CityGridContainer>
						<Typography variant="p2" fontWeight="500">
							cities
						</Typography>
						{locationArray.map((location, index) => (
							<>
								<S.CityWrapper key={index}>
									<Typography
										variant="p2"
										gutterBottom
										className={isMobile ? '' : 'mb-4'}
										fontWeight="700">
										{index + 1}.
									</Typography>
									<FormItem
										label="city"
										required
										isLoading={isLoading}
										colSpan={viewMode ? defaultColSpan : smallColSpan}>
										<EnumSelect
											viewMode={viewMode}
											value={
												cities?.find(
													(city) =>
														city.id.toString() === locationArray?.[index]?.city?.toString()
												)?.name ?? null
											}
											error={errors?.locationArray?.[index]?.city.message}
											setValue={(value) => {
												if (!value) {
													locationArray[index].city = null;
													setValue('locationArray', locationArray, formOptions);
												} else {
													const cityId = cities?.find((city) => city.name === value)?.id;
													locationArray[index].city = cityId?.toString();
													setValue('locationArray', locationArray, formOptions);
												}
											}}
											disabled={!countryName}
											data={cities?.map((city) => city.name)}
										/>
									</FormItem>

									<FormItem
										label="polygon"
										isLoading={isLoading}
										colSpan={viewMode ? defaultColSpan : smallColSpan}
										toolTip="areaform.polygon.tooltip">
										<StringInput
											value={location.polygon}
											disabled={!countryName || viewMode}
											viewMode={viewMode}
											setValue={(value) => {
												locationArray[index].polygon = value;
												setValue('locationArray', locationArray, formOptions);
											}}
											maxLength={maxPolygonLength}
										/>
									</FormItem>
									{!viewMode && (
										<S.ButtonWrapper>
											<Button
												disabled={!countryName || viewMode}
												label={'drawPolygon'}
												onClick={() => handleAddPolygon(index)}
												className="addButton"
												icon={faPlus}
											/>

											<Button
												icon={faTrashCan}
												disabled={!countryName || viewMode || locationArray.length <= 1}
												color="var(--red)"
												variant="transparent"
												onClick={() => handleRemoveCity(index)}
												css={css`
													border-color: var(--red) !important;
												`}
											/>
										</S.ButtonWrapper>
									)}
								</S.CityWrapper>

								{errors?.locationArray?.[index]?.city.message && (
									<InputMessageContainer className="">
										<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
										<Typography variant="caption" color="var(--red)" noWrap>
											{errors?.locationArray?.[index]?.city.message}
										</Typography>
									</InputMessageContainer>
								)}
							</>
						))}
						{!viewMode && (
							<S.AddCityButtonWrapper>
								<Button
									label={'addCity'}
									onClick={handleAddCity}
									className="addButton"
									variant="outline"
									icon={faPlus}
								/>
							</S.AddCityButtonWrapper>
						)}
					</S.CityGridContainer>

					<S.DeliverySlotContainer>
						<FormItem label="deliverySlots" isLoading={isLoading} colSpan={defaultColSpan}>
							<EnumSelect
								viewMode={viewMode}
								name="deliverySlots"
								value={selectedDeliverySlots ?? null}
								setValue={(value) => {
									setSelectedDeliverySlots(value);
									setDeliverySlotIds(value);
								}}
								data={deliverySlotList.map((slot) => slot.name)}
								multipleSelection
							/>
						</FormItem>
					</S.DeliverySlotContainer>
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					<Button
						label={id ? 'save' : 'button.add'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						loading={isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						iconPos="right"
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default CityGroupForm;
